import { useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useDialogChrome } from './dialog.js';
import { t } from '../i18n/index.js';

/**
 * Right-side panel for forms that would otherwise push the page around. Same behaviour as the
 * modal (backdrop, focus trap, Esc closes) but anchored to the edge and full width on phones.
 */
export function SlideOver({
  open,
  title,
  subtitle,
  onClose,
  children,
  width = 460,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useDialogChrome({
    open,
    onClose,
    root: panel,
    // Land on the first field, not the close button (which comes first in the DOM).
    pickInitial: (el) =>
      el
        .querySelector('.slideover-body')
        ?.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
        ),
  });

  if (!open) return null;
  return (
    <div
      className="slideover-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panel}
        className="slideover"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="slideover-title"
        tabIndex={-1}
      >
        <div className="slideover-head">
          <div>
            <h3 id="slideover-title">{title}</h3>
            {subtitle && <p className="muted">{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="slideover-body">{children}</div>
      </div>
    </div>
  );
}
